import { useState } from 'react';
import { ValidationError } from '#api/errors.ts';
import { useSuspenseJoinKeyQuery } from '#api/join-keys.queries.ts';
import { Code } from '#components/atoms/code/index.ts';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import { useDeleteJoinKeyMutation } from './useDeleteJoinKeyMutation.ts';

type Props = {
  joinKeyID: string;
  onCancel: () => void;
  onDelete: () => void;
};

export function DeleteJoinKeyDialog({ joinKeyID, onCancel, onDelete }: Props) {
  const [validationErrors, setValidationErrors] = useState<string[] | null>(null);
  const deleteJoinKey = useDeleteJoinKeyMutation();
  const joinKey = useSuspenseJoinKeyQuery(joinKeyID).data.data;

  // Clears out the validation error message, in case the modal is re-opened.
  function closeModal() {
    setValidationErrors(null);
    onCancel();
  }

  return (
    <ConfirmDialog
      title={<div className="truncate">Revoke {joinKey.name}?</div>}
      disableAccept={deleteJoinKey.isPending}
      acceptButtonText="Revoke"
      onCancel={closeModal}
      onAccept={async () => {
        setValidationErrors(null);

        try {
          await deleteJoinKey.mutateAsync(joinKeyID);
          onDelete();
        } catch (error: unknown) {
          if (error instanceof ValidationError) {
            setValidationErrors(error.errors.map((e) => e.message));
          }
        }
      }}
      shouldRestoreFocusViaFocusScope={true}
    >
      <p className="p mt-0">
        If you revoke this join key, machines can no longer enroll with it. Hosts it already enrolled are not affected.
      </p>
      <p className="p mb-0">
        Are you sure you want to revoke <Code className="ml-6 wrap-anywhere">{joinKey.name}</Code>{' '}
        <Code className="ml-0">({joinKey.id})</Code>?
      </p>

      {validationErrors?.length ?
        validationErrors.map((message, index) => (
          // eslint-disable-next-line react/no-array-index-key -- It's fine, these aren't going to move/change
          <ErrorMessage key={index} className={index === 0 ? 'mt-32' : undefined}>
            {message}
          </ErrorMessage>
        ))
      : null}
    </ConfirmDialog>
  );
}
